import { useBridge } from '@web/adapter/useBridgeRuntime'
import { api } from '@web/api/client'
import { PermissionCard } from '@web/components/PermissionCard'
import { StructuredValue } from '@web/components/tools/StructuredContent'
import { agentMetadata, type ElicitationEntity } from '@web/state/acp'
import { useState } from 'react'
import '@web/components/agent-controls.css'

/** Independent permission and elicitation requests survive replay by request ID. */
export function AcpControls() {
  const { snapshot, sessionId } = useBridge()
  const pending = Object.values(snapshot.bridge.permissions).filter(
    (request) =>
      !request.resolved &&
      (!request.toolUseId || !snapshot.acp.tools[request.toolUseId]),
  )
  const elicitations = Object.values(snapshot.acp.elicitations).filter(
    (request) => !request.resolved,
  )
  const notices = agentMetadata(snapshot.acp).notifications ?? {}
  const authentication = new Map<string, Record<string, unknown>>()
  for (const item of snapshot.acp.extensions) {
    const payload = item.payload
    if (
      payload.type === 'agent/extension' &&
      typeof payload.method === 'string' &&
      notices[payload.method]?.presentation === 'authentication' &&
      payload.params &&
      typeof payload.params === 'object'
    ) {
      authentication.set(
        payload.method,
        payload.params as Record<string, unknown>,
      )
    }
  }
  if (!pending.length && !elicitations.length && !authentication.size)
    return null
  return (
    <section
      aria-label="Agent requests"
      className="agent-requests max-h-[55vh] shrink-0 overflow-y-auto border-b border-line px-4 py-3"
    >
      {[...authentication].map(([method, notice]) => (
        <div
          key={method}
          className="my-2 rounded-control border border-line bg-elevated p-3 text-sm"
          role="status"
        >
          {notice.status === 'requires_action' ? (
            <>
              <p>Sign in to continue</p>
              {typeof notice.authorizationUrl === 'string' &&
                /^https:\/\//.test(notice.authorizationUrl) && (
                  <a
                    className="btn btn-primary btn-sm mt-2"
                    href={notice.authorizationUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open sign-in page
                  </a>
                )}
              <p className="mt-2 text-xs text-muted">
                Complete sign-in in the new page, then return here.
              </p>
            </>
          ) : notice.status === 'completed' ? (
            'Sign-in complete.'
          ) : (
            'Checking sign-in…'
          )}
        </div>
      ))}
      {pending.map((request) => (
        <PermissionCard key={request.requestId} request={request} />
      ))}
      {elicitations.map((request) => (
        <Elicitation
          key={request.requestId}
          entity={request}
          sessionId={sessionId}
          disabled={
            snapshot.bridge.record?.migration_status === 'diagnostic' ||
            snapshot.hydrating ||
            snapshot.socket !== 'open'
          }
        />
      ))}
    </section>
  )
}

function Elicitation({
  entity,
  sessionId,
  disabled,
}: {
  entity: ElicitationEntity
  sessionId: string
  disabled: boolean
}) {
  const request = entity.request as Record<string, any>
  const [values, setValues] = useState<Record<string, unknown>>({})
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState('')
  const schema = request.requestedSchema
  const fields =
    request.mode === 'form' && schema?.type === 'object'
      ? (Object.entries(schema.properties ?? {}) as [
          string,
          Record<string, any>,
        ][])
      : []
  const supported =
    request.mode === 'url' ||
    (request.mode === 'form' &&
      schema?.type === 'object' &&
      fields.every(([, field]) =>
        ['string', 'number', 'integer', 'boolean', 'array'].includes(
          field.type,
        ),
      ))
  const respond = async (action: 'accept' | 'decline' | 'cancel') => {
    setBusy(true)
    setError('')
    try {
      await api.command(sessionId, {
        type: 'elicitation',
        request_id: entity.requestId,
        action,
        ...(action === 'accept' && request.mode === 'form'
          ? { content: values }
          : {}),
      })
      setSent(true)
      setValues({})
    } catch (failure) {
      setError((failure as Error).message)
    } finally {
      setBusy(false)
    }
  }
  return (
    <form
      className="my-2 rounded-control border border-line bg-elevated p-4 text-sm"
      data-elicitation-id={entity.requestId}
      onSubmit={(event) => {
        event.preventDefault()
        void respond('accept')
      }}
    >
      <h3 className="font-semibold">Your input is needed</h3>
      <p className="my-2">{request.message}</p>
      {request.mode === 'url' &&
        typeof request.url === 'string' &&
        /^https?:\/\//.test(request.url) && (
          <a
            className="text-accent underline"
            href={request.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open request
          </a>
        )}
      {supported && request.mode === 'form' && (
        <fieldset className="space-y-3" disabled={disabled || busy || sent}>
          {fields.map(([key, field]) => {
            const choices =
              field.oneOf ??
              field.enum?.map((value: unknown) => ({
                const: value,
                title: String(value),
              }))
            const value = values[key] ?? ''
            return (
              <label className="block text-xs" key={key}>
                <span>
                  {field.title ?? key}
                  {schema.required?.includes(key) ? ' (required)' : ''}
                </span>
                {field.description && (
                  <span className="my-1 block text-muted">
                    {field.description}
                  </span>
                )}
                {field.type === 'boolean' ? (
                  <select
                    className="control mt-1 w-full"
                    required={schema.required?.includes(key)}
                    value={String(value)}
                    onChange={(event) =>
                      setValues((previous) => ({
                        ...previous,
                        [key]: event.target.value === 'true',
                      }))
                    }
                  >
                    <option value="">Choose</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                ) : field.type === 'array' ? (
                  <select
                    className="control mt-1 w-full"
                    multiple
                    value={Array.isArray(value) ? value.map(String) : []}
                    onChange={(event) =>
                      setValues((previous) => ({
                        ...previous,
                        [key]: [...event.target.selectedOptions].map(
                          (option) => option.value,
                        ),
                      }))
                    }
                  >
                    {(
                      field.items?.oneOf ??
                      field.items?.enum?.map((item: string) => ({
                        const: item,
                        title: item,
                      })) ??
                      []
                    ).map((item: { const: string; title: string }) => (
                      <option key={item.const} value={item.const}>
                        {item.title}
                      </option>
                    ))}
                  </select>
                ) : choices ? (
                  <select
                    className="control mt-1 w-full"
                    required={schema.required?.includes(key)}
                    value={String(value)}
                    onChange={(event) =>
                      setValues((previous) => ({
                        ...previous,
                        [key]: event.target.value,
                      }))
                    }
                  >
                    <option value="">Choose</option>
                    {choices.map((choice: { const: string; title: string }) => (
                      <option key={choice.const} value={choice.const}>
                        {choice.title}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    className="control mt-1 w-full"
                    type={
                      field.type === 'string'
                        ? field.format === 'email'
                          ? 'email'
                          : field.format === 'uri'
                            ? 'url'
                            : 'text'
                        : 'number'
                    }
                    step={field.type === 'integer' ? 1 : 'any'}
                    min={field.minimum}
                    max={field.maximum}
                    minLength={field.minLength}
                    maxLength={field.maxLength}
                    pattern={field.pattern}
                    required={schema.required?.includes(key)}
                    value={String(value)}
                    onChange={(event) =>
                      setValues((previous) => ({
                        ...previous,
                        [key]:
                          field.type === 'string'
                            ? event.target.value
                            : Number(event.target.value),
                      }))
                    }
                  />
                )}
              </label>
            )
          })}
        </fieldset>
      )}
      {!supported && (
        <>
          <p className="my-2 text-muted">
            This request cannot be displayed as a supported form.
          </p>
          <details>
            <summary>Request details</summary>
            <StructuredValue value={request} />
          </details>
        </>
      )}
      <div className="mt-3 flex gap-2">
        <button
          className="btn btn-primary"
          type="submit"
          disabled={disabled || busy || sent || !supported}
        >
          Continue
        </button>
        <button
          className="btn"
          type="button"
          disabled={disabled || busy || sent}
          onClick={() => void respond('decline')}
        >
          Decline
        </button>
        <button
          className="btn"
          type="button"
          disabled={disabled || busy || sent}
          onClick={() => void respond('cancel')}
        >
          Cancel
        </button>
      </div>
      {sent && (
        <p role="status" className="mt-2 text-xs">
          Response sent. Waiting for the agent…
        </p>
      )}
      {error && (
        <p role="alert" className="mt-2 text-xs text-danger">
          {error}
        </p>
      )}
    </form>
  )
}
